import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VendorState } from "../../../../redux/vendor/rootReducer";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CabochonRequirements } from "../models/CabochonRequirements.model";
import { useTextureApi } from "../../../../common/hooks/use-texture-api.hook";
import { setLoading } from "../../../../redux/vendor/ui/uiActions";
import {
    GroutColorField,
    PatternFieldLabel,
    PatternSectionTitle,
    SliderWithInput,
    UnitInput,
} from "./PatternFormControls";

// Add interface for component props
interface CabochonPatternFormProps {
  seamlessResult: any;
  setSeamlessResult: any;
  uploadedImages?: any[];
}

// The octogonal and square upload zones are identical apart from which image
// list they manage — one section component, two instances.
const UploadSection = ({ label, browseLabel, addMoreLabel, images, inputRef, onZoneClick, onFilesChange, onRemove, altPrefix }) => (
  <div
    onClick={onZoneClick}
    className={`flex h-full min-h-[140px] cursor-pointer flex-col items-center border-2 border-dashed border-border bg-muted/40 p-4 transition-colors hover:border-foreground/40 hover:bg-muted ${images.length > 0 ? 'justify-start' : 'justify-center'}`}
  >
    {images.length === 0 ? (
      <>
        <Plus className="mb-3 size-8 text-muted-foreground" />
        <p className="mb-1 text-center text-xs font-medium text-foreground">{label}</p>
        <p className="text-center text-xs text-muted-foreground">{browseLabel}</p>
      </>
    ) : (
      <>
        <p className="mb-4 self-start text-xs font-medium text-muted-foreground">{label}</p>
        <div className="flex w-full flex-wrap gap-3">
          {/* Add more button */}
          <div className="flex size-[72px] flex-col items-center justify-center border border-dashed border-border bg-muted/40 transition-colors hover:bg-muted">
            <Plus className="mb-0.5 size-5 text-muted-foreground" />
            <span className="text-center text-[10px] text-muted-foreground">{addMoreLabel}</span>
          </div>

          {/* Image Previews */}
          {images.map((image, index) => (
            <div
              key={index}
              className="relative size-[72px] overflow-visible border border-border transition-transform hover:scale-105"
            >
              <img
                src={URL.createObjectURL(image)}
                alt={`${altPrefix} ${index}`}
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(image); }}
                className="absolute -end-2 -top-2 flex size-6 items-center justify-center rounded-full border border-border bg-background text-base text-muted-foreground transition-colors hover:border-destructive hover:bg-destructive hover:text-white"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </>
    )}
    <input
      type="file"
      onChange={onFilesChange}
      multiple
      accept="image/*"
      ref={inputRef}
      className="hidden"
    />
  </div>
);

export const CabochonPatternForm = ({seamlessResult, setSeamlessResult, uploadedImages = []}: CabochonPatternFormProps) => {
  const { t } = useTranslation();
  const hiddenOctogonalFileInput = useRef(null);
  const hiddenSquareFileInput = useRef(null);
  const { language } = useSelector((state: VendorState) => state.settings.theme);
  const [octogonalImages, setOctogonalImages] = useState([])
  const [squareImages, setSquareImages] = useState([])
  const [isOnEditGroutColor, setIsOnEditGroutColor] = useState(false)
  const textureAPI = useTextureApi()
  const dispatch = useDispatch()

  const [fields, setFields] = useState({
    squareHeight: {
      isTouched: false,
      label: `${t('PATTERNS.PARTIALS.SQUARE_HEIGHT')}`,
      name: "squareHeight",
      value: "",
      unit: "cm"
    },
    octogonalHeight: {
      isTouched: false,
      label: `${t('PATTERNS.PARTIALS.OCTOGONAL_HEIGHT')}`,
      name: "octogonalHeight",
      value: "",
      unit: "cm"
    },
    textureWidth: {
      isTouched: false,
      label: `${t('PATTERNS.PARTIALS.TEXTURE_WIDTH')}`,
      name: "textureWidth",
      value: "2",
      unit: "m"
    },
    groutLine: {
      isTouched: false,
      label: `${t('PATTERNS.PARTIALS.GROUT_LINE_WIDTH')}`,
      name: "groutLine",
      value: "2",
      unit: "mm"
    },
    groutColor: {
      isTouched: false,
      label: `${t('PATTERNS.PARTIALS.GROUT_LINE_COLOR')}`,
      name: "groutColor",
      value: "#CCCCCC"
    }
  });

  const handleClick = (event, type) => {
    if(type === "square"){
      hiddenSquareFileInput.current.click();
    } else {
      hiddenOctogonalFileInput.current.click();
    }
  };

  const handleFilesChange = async(event, type) => {
    const files = event.target.files;
    if(type === "square"){
      setSquareImages([...squareImages, ...Array.from(files)]);
    } else {
      setOctogonalImages([...octogonalImages, ...Array.from(files)]);
    }
  };

  const handleGenerateSeamless = async () => {
    if(isValidForm()){
      try {
        dispatch(setLoading(true))
        const body = new CabochonRequirements()
        body.setGroutLineColor(fields.groutColor.value || "#CCCCCC")
          .setGroutLineWidth(fields.groutLine.value)
          .setOctoImages(octogonalImages)
          .setSquareImages(squareImages)
          .setTextureWidth(fields.textureWidth.value)
          .setOctoHeight(fields.octogonalHeight.value)
          .setSquareHeight(fields.squareHeight.value)
        let data = await textureAPI.generateTextureForPattern(body, "cabochon");
        setSeamlessResult(data)
      } catch (error) {
        console.error('Error generating seamless texture:', error);
        setSeamlessResult(null);
      } finally {
        dispatch(setLoading(false))
      }
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFields({...fields, [name]: {
      ...fields[name],
      value: value,
      isTouched: true
    }});
  };

  const handleSliderChange = (name, value) => {
    setFields({...fields, [name]: {
      ...fields[name],
      value: value.toString(),
      isTouched: true
    }});
  };

  const isValidForm = () => {
    // The API joins the two lists and the worker halves them again: same count each.
    return octogonalImages && octogonalImages.length > 0 &&
      squareImages && squareImages.length === octogonalImages.length &&
      fields.octogonalHeight.value &&
      fields.squareHeight.value;
  };

  const removeImage = (imageToRemove, type) => {
    if(type === "square"){
      setSquareImages(squareImages.filter(image => image.name !== imageToRemove.name));
    } else {
      setOctogonalImages(octogonalImages.filter(image => image.name !== imageToRemove.name));
    }
  };

  const handleGroutColorChange = (color) => {
    setFields({
      ...fields,
      groutColor: {
        ...fields.groutColor,
        value: color.hex + Math.round(color.rgb.a * 255).toString(16)
      }
    });
  };

  return (
    <div>
      {/* Hidden button for programmatic triggering */}
      <button
        type="button"
        id="cabochon-generate-btn"
        onClick={handleGenerateSeamless}
        className="hidden"
      />

      <div className="mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.IMAGES')}</PatternSectionTitle>

        {/* Display both upload sections in a row */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <UploadSection
            label={t('PATTERNS.PARTIALS.UPLOAD_OCTOGONAL')}
            browseLabel={t('PATTERNS.PARTIALS.CLICK_TO_BROWSE')}
            addMoreLabel={t('COMMON.ADD_MORE')}
            images={octogonalImages}
            inputRef={hiddenOctogonalFileInput}
            onZoneClick={(e) => handleClick(e, "octogonal")}
            onFilesChange={(event) => handleFilesChange(event, "octogonal")}
            onRemove={(image) => removeImage(image, "octogonal")}
            altPrefix="Octogonal"
          />
          <UploadSection
            label={t('PATTERNS.PARTIALS.UPLOAD_SQUARE')}
            browseLabel={t('PATTERNS.PARTIALS.CLICK_TO_BROWSE')}
            addMoreLabel={t('COMMON.ADD_MORE')}
            images={squareImages}
            inputRef={hiddenSquareFileInput}
            onZoneClick={(e) => handleClick(e, "square")}
            onFilesChange={(event) => handleFilesChange(event, "square")}
            onRemove={(image) => removeImage(image, "square")}
            altPrefix="Square"
          />
        </div>
        {octogonalImages.length > 0 && squareImages.length > 0 && octogonalImages.length !== squareImages.length && (
          <p className="mt-3 text-xs text-warning">
            {t('PAGES.NEW_PRODUCT.V3.SURFACE.CABOCHON_UNBALANCED', { octagons: octogonalImages.length, squares: squareImages.length })}
          </p>
        )}
      </div>

      {/* Dimensions Section */}
      <div className="mt-12 mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.DIMENSIONS')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.octogonalHeight.label}</PatternFieldLabel>
            <UnitInput
              value={fields.octogonalHeight.value}
              name="octogonalHeight"
              onChange={handleChange}
              type="number"
              unit={fields.octogonalHeight.unit}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.squareHeight.label}</PatternFieldLabel>
            <UnitInput
              value={fields.squareHeight.value}
              name="squareHeight"
              onChange={handleChange}
              type="number"
              unit={fields.squareHeight.unit}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.TEXTURE')}</PatternSectionTitle>

        <div>
          <PatternFieldLabel>{fields.textureWidth.label}</PatternFieldLabel>
          <SliderWithInput
            name="textureWidth"
            value={fields.textureWidth.value}
            min={1}
            max={4}
            step={1}
            markLabels={['1m', '2m', '3m', '4m']}
            showInput={false}
            onSliderChange={handleSliderChange}
          />
        </div>
      </div>

      <div className="mt-8">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.GROUT')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.groutLine.label}</PatternFieldLabel>
            <SliderWithInput
              name="groutLine"
              value={fields.groutLine.value}
              unit={fields.groutLine.unit}
              min={0}
              max={10}
              step={0.5}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.groutColor.label}</PatternFieldLabel>
            <GroutColorField
              color={fields.groutColor.value}
              open={isOnEditGroutColor}
              onOpenChange={setIsOnEditGroutColor}
              onColorChange={handleGroutColorChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
